import React, { useState, useEffect, useMemo } from 'react';
import { 
  ShieldCheck, 
  Sparkles, 
  CheckCircle2, 
  AlertTriangle, 
  AlertOctagon, 
  Info, 
  Search, 
  ArrowRight, 
  RefreshCw, 
  Layers, 
  BookOpen, 
  FileText, 
  Sliders, 
  Check, 
  X, 
  ExternalLink, 
  Clock, 
  TrendingUp, 
  Award, 
  Compass, 
  Cpu, 
  ChevronRight,
  Database,
  History,
  Zap,
  Target
} from 'lucide-react';
import { useThemeTokens } from '../../hooks/useActiveTheme';
import { 
  CertAuditorService, 
  CertOverviewItem, 
  CertAuditPatchItem, 
  CertAuditReportItem, 
  CompetitorBenchmarkData 
} from '../../services/certAuditorService';

export const CertAuditor: React.FC = () => {
  const tokens = useThemeTokens();

  // Navigation & Selected State
  const [certifications, setCertifications] = useState<CertOverviewItem[]>([]);
  const [selectedCertId, setSelectedCertId] = useState<string>('cna');
  const [activeTab, setActiveTab] = useState<'patches' | 'benchmarks' | 'inspector' | 'history'>('patches');
  
  // Scopes & Execution
  const [moduleScope, setModuleScope] = useState<string>('all');
  const [sampleLimit, setSampleLimit] = useState<number>(15);
  const [focusArea, setFocusArea] = useState<string>('');
  
  // Data states
  const [latestReport, setLatestReport] = useState<CertAuditReportItem | null>(null);
  const [patches, setPatches] = useState<CertAuditPatchItem[]>([]);
  const [benchmarkData, setBenchmarkData] = useState<CompetitorBenchmarkData | null>(null);
  const [auditHistory, setAuditHistory] = useState<CertAuditReportItem[]>([]);
  
  // Filtering & Selection
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'moderate' | 'minor'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'applied' | 'rejected'>('pending');
  const [selectedPatchIds, setSelectedPatchIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Loading & Error states
  const [isLoadingOverview, setIsLoadingOverview] = useState<boolean>(true);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [isBenchmarking, setIsBenchmarking] = useState<boolean>(false);
  const [isCommitting, setIsCommitting] = useState<string | null>(null);
  const [isBatchCommitting, setIsBatchCommitting] = useState<boolean>(false);
  const [feedbackToast, setFeedbackToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Single Item Inspector State
  const [inspectorType, setInspectorType] = useState<'questions' | 'flashcards' | 'skills'>('questions');
  const [inspectorStem, setInspectorStem] = useState<string>('During bed bath procedure, what is the critical first action before washing the resident?');
  const [inspectorExplanation, setInspectorExplanation] = useState<string>('Wash hands and explain the procedure to ensure safety.');
  const [inspectorOptions, setInspectorOptions] = useState<string>('Wash hands and put on gloves\nPut on clean apron\nRaise side rails\nCheck room temperature');
  const [isInspecting, setIsInspecting] = useState<boolean>(false);
  const [inspectorResult, setInspectorResult] = useState<any | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setFeedbackToast({ type, message });
    setTimeout(() => setFeedbackToast(null), 4000);
  };

  // Initial Fetch of Certifications Overview
  const fetchOverview = async () => {
    setIsLoadingOverview(true);
    try {
      const data = await CertAuditorService.getOverview();
      if (data.certifications && data.certifications.length > 0) {
        setCertifications(data.certifications);
        if (!data.certifications.some(c => c.certId === selectedCertId)) {
          setSelectedCertId(data.certifications[0].certId);
        }
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to load certifications overview', 'error');
    } finally {
      setIsLoadingOverview(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  // Fetch Patches & History when selectedCertId changes
  const loadCertData = async (certId: string) => {
    try {
      const [patchesRes, historyRes] = await Promise.all([
        CertAuditorService.getPatches(certId, 'all'),
        CertAuditorService.getHistory(certId)
      ]);
      
      if (patchesRes.patches) {
        setPatches(patchesRes.patches);
      }
      if (historyRes.reports && historyRes.reports.length > 0) {
        setAuditHistory(historyRes.reports);
        setLatestReport(historyRes.reports[0]);
      } else {
        setLatestReport(null);
      }
    } catch (err) {
      console.warn('[CertAuditor] Error fetching cert details:', err);
    }
  };

  useEffect(() => {
    if (selectedCertId) {
      loadCertData(selectedCertId);
      setSelectedPatchIds([]);
      setBenchmarkData(null);
    }
  }, [selectedCertId]);

  // Current active cert details
  const activeCert = useMemo(() => {
    return certifications.find(c => c.certId === selectedCertId) || {
      certId: selectedCertId,
      name: selectedCertId.toUpperCase(),
      shortName: selectedCertId.toUpperCase(),
      gatewayCategory: 'Healthcare',
      counts: { questions: 0, flashcards: 0, skills: 0, studyModules: 0, simulators: 0, totalItems: 0 },
      pendingPatchesCount: 0
    };
  }, [certifications, selectedCertId]);

  // Execute Clinical Audit
  const handleExecuteScan = async () => {
    setIsScanning(true);
    try {
      const result = await CertAuditorService.runScan({
        certId: selectedCertId,
        moduleScope,
        sampleLimit,
        focusArea
      });

      if (result.success) {
        setLatestReport(result.report);
        // Refresh patches & history
        await loadCertData(selectedCertId);
        await fetchOverview();
        showToast(`Clinical audit complete! ${result.patches.length} quality patches generated.`, 'success');
      }
    } catch (err: any) {
      showToast(err.message || 'Audit execution failed', 'error');
    } finally {
      setIsScanning(false);
    }
  };

  // Execute Competitor & Board Standards Scan
  const handleScanBenchmarks = async () => {
    setIsBenchmarking(true);
    try {
      const result = await CertAuditorService.scanCompetitorBenchmarks(selectedCertId);
      if (result.success) {
        setBenchmarkData(result.benchmark);
        setActiveTab('benchmarks');
        showToast('Board standards and competitor curriculum benchmark synchronized!', 'success');
      }
    } catch (err: any) {
      showToast(err.message || 'Benchmark scan failed', 'error');
    } finally {
      setIsBenchmarking(false);
    }
  };

  // Commit Single Patch
  const handleCommitPatch = async (patchId: string) => {
    setIsCommitting(patchId);
    try {
      const res = await CertAuditorService.commitPatch(patchId);
      if (res.success) {
        setPatches(prev => prev.map(p => p._id === patchId ? { ...p, status: 'applied', appliedAt: new Date().toISOString() } : p));
        setSelectedPatchIds(prev => prev.filter(id => id !== patchId));
        showToast('Patch committed directly to curriculum database!', 'success');
        fetchOverview();
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to commit patch', 'error');
    } finally {
      setIsCommitting(null);
    }
  };

  // Reject / Dismiss Patch
  const handleRejectPatch = async (patchId: string) => {
    try {
      const res = await CertAuditorService.rejectPatch(patchId, 'Dismissed by Super Admin');
      if (res.success) {
        setPatches(prev => prev.map(p => p._id === patchId ? { ...p, status: 'rejected' } : p));
        setSelectedPatchIds(prev => prev.filter(id => id !== patchId));
        showToast('Patch dismissed.', 'info');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to dismiss patch', 'error');
    }
  };

  // Commit Batch
  const handleBatchCommit = async () => {
    if (selectedPatchIds.length === 0) return;
    setIsBatchCommitting(true);
    try {
      const res = await CertAuditorService.commitBatch(selectedPatchIds);
      if (res.success) {
        setPatches(prev => prev.map(p => selectedPatchIds.includes(p._id) ? { ...p, status: 'applied', appliedAt: new Date().toISOString() } : p));
        showToast(`Successfully committed ${res.appliedCount} patches to live database!`, 'success');
        setSelectedPatchIds([]);
        fetchOverview();
      }
    } catch (err: any) {
      showToast(err.message || 'Batch commit failed', 'error');
    } finally {
      setIsBatchCommitting(false);
    }
  };

  // Run On-Demand Single Item Audit
  const handleInspectSingleItem = async () => {
    setIsInspecting(true);
    try {
      const formattedOptions = inspectorOptions
        .split('\n')
        .filter(Boolean)
        .map((opt, i) => ({ text: opt, isCorrect: i === 0 }));

      const itemPayload = {
        text: inspectorStem,
        explanation: inspectorExplanation,
        options: formattedOptions,
        category: 'Clinical Core Practice'
      };

      const res = await CertAuditorService.auditSingleItem(selectedCertId, inspectorType, itemPayload);
      if (res.success) {
        setInspectorResult(res.audit);
        showToast('Item evaluated by Clinical Master Educator!', 'success');
      }
    } catch (err: any) {
      showToast(err.message || 'Item audit failed', 'error');
    } finally {
      setIsInspecting(false);
    }
  };

  // Filtered Patches
  const filteredPatches = useMemo(() => {
    return patches.filter(p => {
      if (severityFilter !== 'all' && p.severity !== severityFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = p.itemTitle?.toLowerCase().includes(q);
        const matchFinding = p.finding?.toLowerCase().includes(q);
        const matchCitation = p.clinicalCitation?.toLowerCase().includes(q);
        if (!matchTitle && !matchFinding && !matchCitation) return false;
      }
      return true;
    });
  }, [patches, severityFilter, statusFilter, searchQuery]);

  // Select / Deselect All
  const handleToggleSelectAll = () => {
    const pendingIds = filteredPatches.filter(p => p.status === 'pending').map(p => p._id);
    if (selectedPatchIds.length === pendingIds.length && pendingIds.length > 0) {
      setSelectedPatchIds([]);
    } else {
      setSelectedPatchIds(pendingIds);
    }
  };

  return (
    <div id="cert-auditor-root" className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 pb-20">
      
      {/* Toast Notification */}
      {feedbackToast && (
        <div id="cert-auditor-toast" className="fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border backdrop-blur-md transition-all animate-in fade-in slide-in-from-top-4 bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-800">
          {feedbackToast.type === 'success' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          {feedbackToast.type === 'error' && <AlertOctagon className="w-5 h-5 text-rose-500" />}
          {feedbackToast.type === 'info' && <Info className="w-5 h-5 text-blue-500" />}
          <span className="text-sm font-semibold">{feedbackToast.message}</span>
        </div>
      )}

      {/* Main Header & Global Action Bar */}
      <div id="cert-auditor-header-banner" className={`${tokens.heroBannerBg} border-b border-slate-200 dark:border-slate-800 px-6 py-8`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${tokens.iconBg} text-white shadow-sm`}>
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className={`text-2xl font-bold tracking-tight ${tokens.headerFont}`}>
                    Cert Auditor
                  </h1>
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${tokens.badge}`}>
                    Super Admin Quality Suite
                  </span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  AI Master Clinical Educator & Curriculum Evaluator. Grounded in 2024–2026 National Board Standards.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats & Active Target */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-white/80 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 px-4 py-2 rounded-xl backdrop-blur-xs">
              <Database className="w-4 h-4 text-slate-500" />
              <div className="text-xs">
                <span className="text-slate-500 block">Total Curriculum Items</span>
                <span className="font-bold text-slate-900 dark:text-slate-100">
                  {activeCert.counts.totalItems.toLocaleString()} items
                </span>
              </div>
            </div>

            <button
              id="btn-scan-competitors"
              onClick={handleScanBenchmarks}
              disabled={isBenchmarking || isScanning}
              className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 cursor-pointer ${tokens.secondaryBtn}`}
            >
              <Compass className={`w-4 h-4 ${isBenchmarking ? 'animate-spin' : ''}`} />
              <span>{isBenchmarking ? 'Scanning Boards...' : 'Board & Competitor Intel'}</span>
            </button>

            <button
              id="btn-execute-clinical-audit"
              onClick={handleExecuteScan}
              disabled={isScanning}
              className={`px-5 py-2.5 text-sm font-semibold flex items-center gap-2 cursor-pointer shadow-md ${tokens.primaryBtn}`}
            >
              <Sparkles className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
              <span>{isScanning ? 'Auditing Curriculum...' : 'Run Clinical AI Audit'}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-6 space-y-6">

        {/* Dynamic Certification Selector Bar */}
        <div id="cert-selector-bar" className={`${tokens.cardBg} p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4`}>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Active Certification:
            </label>
            <select
              id="select-cert-id"
              value={selectedCertId}
              onChange={(e) => setSelectedCertId(e.target.value)}
              className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-bold text-sm px-3.5 py-2 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              {certifications.map((c) => (
                <option key={c.certId} value={c.certId}>
                  {c.name} ({c.gatewayCategory}) — {c.counts.totalItems} items
                </option>
              ))}
            </select>

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />

            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Target Module:
            </label>
            <select
              id="select-module-scope"
              value={moduleScope}
              onChange={(e) => setModuleScope(e.target.value)}
              className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium px-3 py-2 rounded-xl focus:outline-hidden"
            >
              <option value="all">All Modules (Questions, Flashcards, Skills)</option>
              <option value="questions">Practice Questions Only</option>
              <option value="flashcards">Flashcards Only</option>
              <option value="skills">Clinical Skills Checklists</option>
              <option value="studymodules">Study Guide Modules</option>
              <option value="simulatorscenarios">Clinical Simulator Scenarios</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span>Sample Depth:</span>
              <input 
                id="input-sample-limit"
                type="number"
                min={5}
                max={50}
                value={sampleLimit}
                onChange={(e) => setSampleLimit(parseInt(e.target.value) || 15)}
                className="w-14 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded text-center font-bold text-xs"
              />
            </div>

            <input
              id="input-focus-area"
              type="text"
              placeholder="Optional focus (e.g. Infection Control, Vitals)"
              value={focusArea}
              onChange={(e) => setFocusArea(e.target.value)}
              className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-xl text-xs w-56 placeholder-slate-400 focus:outline-hidden"
            />
          </div>
        </div>

        {/* Quality Scorecards & Diagnostic Metrics */}
        <div id="cert-auditor-scorecards" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          {/* Overall Health Card */}
          <div className={`${tokens.cardBg} p-5 relative overflow-hidden flex flex-col justify-between`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Curriculum Health Score
              </span>
              <Award className={`w-5 h-5 ${tokens.accentText}`} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-slate-50">
                {latestReport ? `${latestReport.healthScore}%` : '92%'}
              </span>
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                Grade A-
              </span>
            </div>
            <div className="mt-3 w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
              <div 
                className={`h-full ${tokens.progressBar}`} 
                style={{ width: `${latestReport ? latestReport.healthScore : 92}%` }} 
              />
            </div>
            <span className="text-[11px] text-slate-400 mt-2">
              {latestReport ? `Last evaluated ${new Date(latestReport.createdAt).toLocaleDateString()}` : 'Default baseline evaluation'}
            </span>
          </div>

          {/* Clinical Accuracy Card */}
          <div className={`${tokens.cardBg} p-5 relative overflow-hidden flex flex-col justify-between`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Clinical Accuracy Index
              </span>
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-slate-50">
                {latestReport ? `${latestReport.clinicalAccuracyScore}%` : '95%'}
              </span>
              <span className="text-xs font-medium text-slate-500">CDC/AHA 2025</span>
            </div>
            <div className="mt-3 w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500" 
                style={{ width: `${latestReport ? latestReport.clinicalAccuracyScore : 95}%` }} 
              />
            </div>
            <span className="text-[11px] text-slate-400 mt-2">
              Strict compliance with latest isolation & BLS
            </span>
          </div>

          {/* Distractor Rigor Card */}
          <div className={`${tokens.cardBg} p-5 relative overflow-hidden flex flex-col justify-between`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Distractor Rigor Score
              </span>
              <Cpu className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-slate-50">
                {latestReport ? `${latestReport.distractorRigorScore}%` : '88%'}
              </span>
              <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Plausibility
              </span>
            </div>
            <div className="mt-3 w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500" 
                style={{ width: `${latestReport ? latestReport.distractorRigorScore : 88}%` }} 
              />
            </div>
            <span className="text-[11px] text-slate-400 mt-2">
              Filters obvious throwaways & non-viable options
            </span>
          </div>

          {/* Staged Patches Action Card */}
          <div className={`${tokens.cardBg} p-5 relative overflow-hidden flex flex-col justify-between border-l-4 border-l-blue-500`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Pending Clinical Patches
              </span>
              <Zap className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold text-blue-600 dark:text-blue-400">
                {patches.filter(p => p.status === 'pending').length}
              </span>
              <span className="text-xs text-slate-500">ready to commit</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-300 font-bold">
                {patches.filter(p => p.status === 'pending' && p.severity === 'critical').length} Critical
              </span>
              <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300 font-bold">
                {patches.filter(p => p.status === 'pending' && p.severity === 'moderate').length} Moderate
              </span>
            </div>
            <button
              onClick={() => setActiveTab('patches')}
              className="mt-2 text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>Review Diff Inspector</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

        </div>

        {/* Cognitive Balance & Benchmark Pill Strip */}
        {latestReport?.cognitiveDistribution && (
          <div id="cognitive-distribution-strip" className={`${tokens.cardBg} p-4 flex flex-col md:flex-row md:items-center justify-between gap-4`}>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Bloom's Taxonomy Cognitive Distribution:
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs font-semibold">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
                <span>Direct Recall: {latestReport.cognitiveDistribution.recall}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                <span>Procedural Application: {latestReport.cognitiveDistribution.application}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span>Clinical Judgment (NGN): {latestReport.cognitiveDistribution.clinicalJudgment}%</span>
              </div>
            </div>
            <span className="text-xs text-slate-400 italic">
              Target: ≥20% Clinical Judgment for Board Alignment
            </span>
          </div>
        )}

        {/* Main Tab Navigation */}
        <div id="cert-auditor-tabs" className="border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              id="tab-btn-patches"
              onClick={() => setActiveTab('patches')}
              className={`px-5 py-3 text-sm font-bold flex items-center gap-2 cursor-pointer transition-all border-b-2 ${
                activeTab === 'patches'
                  ? `${tokens.accentBorder} text-blue-600 dark:text-blue-400 border-b-2`
                  : 'border-b-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Staged Patches & Diffs ({patches.filter(p => p.status === 'pending').length})</span>
            </button>

            <button
              id="tab-btn-benchmarks"
              onClick={() => setActiveTab('benchmarks')}
              className={`px-5 py-3 text-sm font-bold flex items-center gap-2 cursor-pointer transition-all border-b-2 ${
                activeTab === 'benchmarks'
                  ? `${tokens.accentBorder} text-blue-600 dark:text-blue-400 border-b-2`
                  : 'border-b-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Compass className="w-4 h-4" />
              <span>Competitor & Board Intel</span>
            </button>

            <button
              id="tab-btn-inspector"
              onClick={() => setActiveTab('inspector')}
              className={`px-5 py-3 text-sm font-bold flex items-center gap-2 cursor-pointer transition-all border-b-2 ${
                activeTab === 'inspector'
                  ? `${tokens.accentBorder} text-blue-600 dark:text-blue-400 border-b-2`
                  : 'border-b-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Cpu className="w-4 h-4" />
              <span>Live Item Inspector</span>
            </button>

            <button
              id="tab-btn-history"
              onClick={() => setActiveTab('history')}
              className={`px-5 py-3 text-sm font-bold flex items-center gap-2 cursor-pointer transition-all border-b-2 ${
                activeTab === 'history'
                  ? `${tokens.accentBorder} text-blue-600 dark:text-blue-400 border-b-2`
                  : 'border-b-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <History className="w-4 h-4" />
              <span>Audit Ledger ({auditHistory.length})</span>
            </button>
          </div>
        </div>

        {/* TAB 1: STAGED PATCHES & DIFFS */}
        {activeTab === 'patches' && (
          <div id="tab-content-patches" className="space-y-4">
            
            {/* Filter & Batch Actions Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                  <input
                    id="input-search-patches"
                    type="text"
                    placeholder="Search findings, citations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-3 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs w-52 focus:outline-hidden"
                  />
                </div>

                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg text-xs font-semibold">
                  <button
                    onClick={() => setSeverityFilter('all')}
                    className={`px-2.5 py-1 rounded cursor-pointer ${severityFilter === 'all' ? 'bg-white dark:bg-slate-700 shadow-xs' : 'text-slate-500'}`}
                  >
                    All Severities
                  </button>
                  <button
                    onClick={() => setSeverityFilter('critical')}
                    className={`px-2.5 py-1 rounded cursor-pointer text-rose-600 ${severityFilter === 'critical' ? 'bg-white dark:bg-slate-700 shadow-xs' : ''}`}
                  >
                    Critical
                  </button>
                  <button
                    onClick={() => setSeverityFilter('moderate')}
                    className={`px-2.5 py-1 rounded cursor-pointer text-amber-600 ${severityFilter === 'moderate' ? 'bg-white dark:bg-slate-700 shadow-xs' : ''}`}
                  >
                    Moderate
                  </button>
                  <button
                    onClick={() => setSeverityFilter('minor')}
                    className={`px-2.5 py-1 rounded cursor-pointer text-slate-600 ${severityFilter === 'minor' ? 'bg-white dark:bg-slate-700 shadow-xs' : ''}`}
                  >
                    Minor
                  </button>
                </div>

                <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg text-xs font-semibold">
                  <button
                    onClick={() => setStatusFilter('pending')}
                    className={`px-2.5 py-1 rounded cursor-pointer ${statusFilter === 'pending' ? 'bg-white dark:bg-slate-700 shadow-xs text-blue-600' : 'text-slate-500'}`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setStatusFilter('applied')}
                    className={`px-2.5 py-1 rounded cursor-pointer ${statusFilter === 'applied' ? 'bg-white dark:bg-slate-700 shadow-xs text-emerald-600' : 'text-slate-500'}`}
                  >
                    Applied
                  </button>
                  <button
                    onClick={() => setStatusFilter('all')}
                    className={`px-2.5 py-1 rounded cursor-pointer ${statusFilter === 'all' ? 'bg-white dark:bg-slate-700 shadow-xs' : 'text-slate-500'}`}
                  >
                    All Statuses
                  </button>
                </div>
              </div>

              {/* Batch Actions */}
              <div className="flex items-center gap-3">
                <button
                  id="btn-toggle-select-all"
                  onClick={handleToggleSelectAll}
                  className="text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-900 cursor-pointer"
                >
                  {selectedPatchIds.length > 0 ? 'Deselect All' : 'Select All Pending'}
                </button>

                {selectedPatchIds.length > 0 && (
                  <button
                    id="btn-batch-commit"
                    onClick={handleBatchCommit}
                    disabled={isBatchCommitting}
                    className={`px-4 py-2 text-xs font-bold flex items-center gap-2 cursor-pointer shadow-sm ${tokens.primaryBtn}`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>{isBatchCommitting ? 'Committing...' : `Commit Selected (${selectedPatchIds.length})`}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Patches List */}
            {filteredPatches.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <h3 className="font-bold text-base">No pending curriculum patches found</h3>
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Either all proposed clinical improvements have been committed to MongoDB, or you have not yet run a clinical audit on this certification.
                </p>
                <button
                  onClick={handleExecuteScan}
                  className={`mt-2 px-4 py-2 text-xs font-bold cursor-pointer ${tokens.primaryBtn}`}
                >
                  Run Clinical Audit Now
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPatches.map((patch) => {
                  const isSelected = selectedPatchIds.includes(patch._id);
                  const isPending = patch.status === 'pending';
                  const isApplied = patch.status === 'applied';

                  return (
                    <div
                      key={patch._id}
                      id={`patch-card-${patch._id}`}
                      className={`${tokens.cardBg} p-5 border transition-all ${
                        isSelected ? 'ring-2 ring-blue-500' : ''
                      } ${isApplied ? 'opacity-85' : ''}`}
                    >
                      {/* Card Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                          {isPending && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedPatchIds(prev => 
                                  prev.includes(patch._id) 
                                    ? prev.filter(id => id !== patch._id) 
                                    : [...prev, patch._id]
                                );
                              }}
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider ${
                                patch.severity === 'critical'
                                  ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                                  : patch.severity === 'moderate'
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                  : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                              }`}>
                                {patch.severity}
                              </span>

                              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 uppercase">
                                {patch.targetCollection}
                              </span>

                              <span className="text-xs text-slate-400">
                                Category: {patch.issueCategory.replace('_', ' ')}
                              </span>
                            </div>
                            <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 mt-1">
                              {patch.itemTitle}
                            </h4>
                          </div>
                        </div>

                        {/* Status & Single Commit Actions */}
                        <div className="flex items-center gap-2">
                          {isApplied ? (
                            <span className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/50 px-3 py-1 rounded-lg">
                              <Check className="w-3.5 h-3.5" />
                              <span>Committed to Live DB</span>
                            </span>
                          ) : patch.status === 'rejected' ? (
                            <span className="text-xs font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-lg">
                              Dismissed
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleRejectPatch(patch._id)}
                                className="p-1.5 text-slate-400 hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
                                title="Dismiss patch"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCommitPatch(patch._id)}
                                disabled={isCommitting === patch._id}
                                className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 cursor-pointer shadow-xs ${tokens.primaryBtn}`}
                              >
                                <Check className="w-3.5 h-3.5" />
                                <span>{isCommitting === patch._id ? 'Applying...' : 'Approve & Commit'}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Finding & Citation Details */}
                      <div className="mt-3 space-y-2">
                        <div className="text-xs text-slate-700 dark:text-slate-300">
                          <span className="font-bold text-slate-900 dark:text-slate-100">Clinical Finding: </span>
                          {patch.finding}
                        </div>

                        {patch.clinicalCitation && (
                          <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                            <BookOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="font-semibold">Board Citation:</span>
                            <span>{patch.clinicalCitation}</span>
                          </div>
                        )}
                      </div>

                      {/* Visual Diff Viewer */}
                      {patch.diff && patch.diff.length > 0 && (
                        <div className="mt-4 bg-slate-50 dark:bg-slate-950/60 rounded-xl p-3 border border-slate-200 dark:border-slate-800/80 space-y-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between">
                            <span>Field Mutation Diff:</span>
                            <span className="text-[10px] text-slate-400">Target Document: #{patch.targetDocumentId.substring(0, 10)}</span>
                          </div>

                          {patch.diff.map((df, idx) => (
                            <div key={idx} className="space-y-2 text-xs font-mono">
                              <div className="bg-rose-50/80 dark:bg-rose-950/25 border border-rose-200 dark:border-rose-900/40 p-2.5 rounded-lg text-rose-900 dark:text-rose-300">
                                <span className="text-[10px] font-bold uppercase text-rose-600 block mb-0.5">
                                  - Current Value in DB ({df.field})
                                </span>
                                <div className="text-xs break-words">
                                  {typeof df.before === 'object' ? JSON.stringify(df.before, null, 2) : String(df.before || '(empty)')}
                                </div>
                              </div>

                              <div className="bg-emerald-50/80 dark:bg-emerald-950/25 border border-emerald-200 dark:border-emerald-900/40 p-2.5 rounded-lg text-emerald-900 dark:text-emerald-300">
                                <span className="text-[10px] font-bold uppercase text-emerald-600 block mb-0.5">
                                  + Master Educator Proposal ({df.field})
                                </span>
                                <div className="text-xs break-words font-semibold">
                                  {typeof df.after === 'object' ? JSON.stringify(df.after, null, 2) : String(df.after)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: COMPETITOR & BOARD INTELLIGENCE */}
        {activeTab === 'benchmarks' && (
          <div id="tab-content-benchmarks" className="space-y-6">
            
            {/* Header / Trigger */}
            <div className={`${tokens.cardBg} p-6 flex flex-col md:flex-row md:items-center justify-between gap-4`}>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Compass className="w-5 h-5 text-blue-500" />
                  <span>National Testing Standards & Competitor Grounding: {activeCert.name}</span>
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Continuously grounded against Credentia, Prometric, Pearson VUE, and top commercial test prep vendors.
                </p>
              </div>

              <button
                onClick={handleScanBenchmarks}
                disabled={isBenchmarking}
                className={`px-4 py-2 text-xs font-bold flex items-center gap-2 cursor-pointer ${tokens.primaryBtn}`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isBenchmarking ? 'animate-spin' : ''}`} />
                <span>{isBenchmarking ? 'Scanning...' : 'Synchronize Board Standards'}</span>
              </button>
            </div>

            {/* Benchmark Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Domain Breakdown */}
              <div className={`${tokens.cardBg} p-5 space-y-4`}>
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-blue-500" />
                    <span>Official Candidate Test Plan Weights</span>
                  </h4>
                  <span className="text-xs text-slate-400">Board Consensus</span>
                </div>

                <div className="space-y-3">
                  {(benchmarkData?.domainWeights || [
                    { domain: 'Physical Care Skills & Vital Signs', weight: '54%' },
                    { domain: 'Infection Control & Patient Safety', weight: '22%' },
                    { domain: 'Psychosocial Care & Resident Rights', weight: '14%' },
                    { domain: 'Legal Scope, HIPAA & Professionalism', weight: '10%' }
                  ]).map((item, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between text-xs font-semibold">
                        <span>{item.domain}</span>
                        <span className="text-blue-600 dark:text-blue-400 font-bold">{item.weight}</span>
                      </div>
                      <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500" 
                          style={{ width: item.weight }} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Critical Automatic-Fail Focal Points */}
              <div className={`${tokens.cardBg} p-5 space-y-4 border-l-4 border-l-rose-500`}>
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-500" />
                    <span>High-Yield "Critical Fail" Exam Focal Points</span>
                  </h4>
                  <span className="text-xs text-rose-600 font-bold uppercase">Zero-Tolerance</span>
                </div>

                <div className="space-y-2.5">
                  {(benchmarkData?.criticalFailFocalPoints || [
                    "Failure to lock wheelchair & bed brakes prior to resident transfer",
                    "Breaching sterile field or failing hand hygiene before clean procedures",
                    "Leaving bed in high position or side rail unfastened during patient care",
                    "Failing to confirm resident identity using 2 independent identifiers"
                  ]).map((point, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-1.5 shrink-0" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2024-2026 Regulatory & Procedural Revisions */}
              <div className={`${tokens.cardBg} p-5 space-y-4`}>
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span>Recent 2024–2026 Regulatory Updates</span>
                  </h4>
                  <span className="text-xs text-emerald-600 font-semibold">Active Guidelines</span>
                </div>

                <div className="space-y-2.5">
                  {(benchmarkData?.recentRegulatoryChanges || [
                    "CDC 2024 Isolation Precautions: Hand hygiene before touching PPE supplies",
                    "AHA 2025 BLS updates: Continuous high-fraction chest compression metrics",
                    "HIPAA Digital Privacy: Facility-wide ban on unauthorized mobile camera usage",
                    "Next-Gen Clinical Judgment model prioritization for entry-level nursing"
                  ]).map((change, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300 bg-emerald-50/50 dark:bg-emerald-950/20 p-2.5 rounded-lg border border-emerald-100 dark:border-emerald-900/30">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{change}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Competitor Pedagogy Strengths */}
              <div className={`${tokens.cardBg} p-5 space-y-4`}>
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span>Leading Competitor Pedagogy Strengths</span>
                  </h4>
                  <span className="text-xs text-slate-400">Market Advantage</span>
                </div>

                <div className="space-y-2.5">
                  {(benchmarkData?.competitorPedagogyStrengths || [
                    "Itemized distractor breakdowns explaining clinical 'trap' why wrong is dangerous",
                    "Audio medical terminology pronunciation for ESL students",
                    "Color-coded critical checkpoints on clinical skill procedures",
                    "Timed diagnostic simulations matching real Prometric/Credentia interfaces"
                  ]).map((strength, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-slate-300">
                      <ChevronRight className="w-3.5 h-3.5 text-purple-500 shrink-0 mt-0.5" />
                      <span>{strength}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        )}

        {/* TAB 3: LIVE ITEM INSPECTOR */}
        {activeTab === 'inspector' && (
          <div id="tab-content-inspector" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Input Form */}
            <div className={`${tokens.cardBg} p-6 space-y-4`}>
              <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-blue-500" />
                  <span>Real-Time Clinical Item Evaluator</span>
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Draft or paste any question or flashcard to get instant educator critique, distractor review, and automated repair.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">
                    Item Type:
                  </label>
                  <select
                    value={inspectorType}
                    onChange={(e: any) => setInspectorType(e.target.value)}
                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold px-3 py-2 rounded-xl focus:outline-hidden"
                  >
                    <option value="questions">Practice Question</option>
                    <option value="flashcards">Flashcard</option>
                    <option value="skills">Clinical Skill Procedure</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">
                    Question Stem / Flashcard Front:
                  </label>
                  <textarea
                    rows={3}
                    value={inspectorStem}
                    onChange={(e) => setInspectorStem(e.target.value)}
                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs p-3 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {inspectorType === 'questions' && (
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">
                      Options (First option is correct, one per line):
                    </label>
                    <textarea
                      rows={4}
                      value={inspectorOptions}
                      onChange={(e) => setInspectorOptions(e.target.value)}
                      className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs p-3 rounded-xl focus:outline-hidden font-mono"
                    />
                  </div>
                )}

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">
                    Explanation / Clinical Rationale:
                  </label>
                  <textarea
                    rows={3}
                    value={inspectorExplanation}
                    onChange={(e) => setInspectorExplanation(e.target.value)}
                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs p-3 rounded-xl focus:outline-hidden"
                  />
                </div>

                <button
                  id="btn-inspect-item"
                  onClick={handleInspectSingleItem}
                  disabled={isInspecting}
                  className={`w-full py-2.5 text-xs font-bold flex items-center justify-center gap-2 cursor-pointer shadow-sm ${tokens.primaryBtn}`}
                >
                  <Sparkles className={`w-4 h-4 ${isInspecting ? 'animate-spin' : ''}`} />
                  <span>{isInspecting ? 'Evaluating with Clinical AI...' : 'Audit This Item'}</span>
                </button>
              </div>
            </div>

            {/* Output / Feedback */}
            <div className={`${tokens.cardBg} p-6 space-y-4`}>
              <div className="border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center justify-between">
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-500" />
                  <span>Educator Evaluation & Repair</span>
                </h3>
                {inspectorResult && (
                  <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${
                    inspectorResult.score >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    Score: {inspectorResult.score}/100
                  </span>
                )}
              </div>

              {!inspectorResult ? (
                <div className="p-12 text-center text-slate-400 space-y-2">
                  <Sliders className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-700" />
                  <p className="text-xs">Click "Audit This Item" to receive immediate pedagogical analysis.</p>
                </div>
              ) : (
                <div className="space-y-4 text-xs">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 dark:bg-slate-800/60 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
                      <span className="text-[10px] uppercase text-slate-400 font-bold block">Status</span>
                      <span className="font-bold text-slate-900 dark:text-slate-100">{inspectorResult.rating}</span>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800/60 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
                      <span className="text-[10px] uppercase text-slate-400 font-bold block">Bloom's Level</span>
                      <span className="font-bold text-blue-600 dark:text-blue-400">{inspectorResult.bloomLevel}</span>
                    </div>
                  </div>

                  {inspectorResult.strengths?.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-emerald-600">Strengths:</span>
                      {inspectorResult.strengths.map((st: string, idx: number) => (
                        <div key={idx} className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                          <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <span>{st}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {inspectorResult.weaknesses?.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-rose-600">Clinical Weaknesses:</span>
                      {inspectorResult.weaknesses.map((wk: string, idx: number) => (
                        <div key={idx} className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                          <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                          <span>{wk}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-blue-50/80 dark:bg-blue-950/30 p-3 rounded-xl border border-blue-200 dark:border-blue-900/40">
                    <span className="text-[10px] font-bold uppercase text-blue-700 dark:text-blue-300 block mb-1">
                      Recommendations:
                    </span>
                    <p className="text-slate-800 dark:text-slate-200">{inspectorResult.recommendations}</p>
                  </div>

                  {inspectorResult.improvedItem?.explanation && (
                    <div className="bg-emerald-50/80 dark:bg-emerald-950/30 p-3 rounded-xl border border-emerald-200 dark:border-emerald-900/40">
                      <span className="text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300 block mb-1">
                        Board-Aligned Improved Explanation:
                      </span>
                      <p className="text-emerald-900 dark:text-emerald-200 font-semibold">{inspectorResult.improvedItem.explanation}</p>
                      
                      <button
                        onClick={() => {
                          setInspectorExplanation(inspectorResult.improvedItem.explanation);
                          showToast('Explanation updated with educator recommendation!', 'success');
                        }}
                        className="mt-2 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 hover:underline cursor-pointer"
                      >
                        Apply this explanation to editor
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        )}

        {/* TAB 4: AUDIT HISTORY & LEDGER */}
        {activeTab === 'history' && (
          <div id="tab-content-history" className="space-y-4">
            <div className={`${tokens.cardBg} p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between`}>
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-500" />
                  <span>Curriculum Audit Execution Ledger</span>
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Historical audit runs, health score changes, and verified curriculum fixes for {activeCert.name}.
                </p>
              </div>
            </div>

            {auditHistory.length === 0 ? (
              <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <p className="text-xs text-slate-400">No past audit runs recorded for this certification.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditHistory.map((report) => (
                  <div key={report._id} className={`${tokens.cardBg} p-4 flex flex-col md:flex-row md:items-center justify-between gap-4`}>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-slate-900 dark:text-slate-100">
                          Audit Run #{report._id.substring(report._id.length - 6)}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(report.createdAt).toLocaleString()}
                        </span>
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          Scope: {report.targetModule}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 max-w-2xl">
                        {report.summaryText || 'Curriculum health audit executed successfully.'}
                      </p>
                    </div>

                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <span className="text-xs text-slate-400 block">Health Score</span>
                        <span className="font-bold text-base text-slate-900 dark:text-slate-100">
                          {report.healthScore}%
                        </span>
                      </div>

                      <div className="text-right">
                        <span className="text-xs text-slate-400 block">Issues Flagged</span>
                        <span className="font-bold text-xs text-rose-600">
                          {report.criticalIssuesCount} critical / {report.moderateIssuesCount} mod
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default CertAuditor;
